import { useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { evaluatePassword } from '../../lib/passwordPolicy';

interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  /** Show the live policy checklist below the field — on for create/reset, off for sign-in. */
  showPolicy?: boolean;
  label?: string;
}

/**
 * Password input with show/hide toggle, lock icon, and (optional) live policy checklist.
 * Mirrors backend password policy in `passwordPolicy.ts` — server is still source of truth.
 */
export function PasswordField({
  value,
  onChange,
  showPolicy = false,
  label,
  placeholder = 'Password',
  ...rest
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const checks = showPolicy ? evaluatePassword(value) : null;

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted block">
          {label}
        </label>
      )}
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        <input
          {...rest}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={rest.autoComplete ?? 'current-password'}
          className="w-full pl-12 pr-12 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all font-medium"
        />
        <button
          type="button"
          aria-label={visible ? 'Hide password' : 'Show password'}
          onClick={() => setVisible((v) => !v)}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-brand-primary transition-colors"
        >
          {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
      {checks && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs pt-1">
          {checks.map((c) => (
            <li
              key={c.id}
              className={`flex items-center gap-2 transition-colors ${
                c.passes ? 'text-brand-deep' : 'text-gray-400'
              }`}
            >
              <span
                aria-hidden
                className={`inline-block w-3 h-3 rounded-full border ${
                  c.passes ? 'bg-brand-accent border-brand-accent' : 'border-gray-300'
                }`}
              />
              {c.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
